/**
 * Recently Viewed Venues Hook
 *
 * Manages recently viewed venues in localStorage with a configurable limit.
 * Provides methods to add, get, and clear recently viewed venues.
 */

import { useState, useCallback, useEffect } from 'react';

export interface RecentlyViewedVenue {
  id: string;
  name: string;
  slug: string;
  coverImage: string | null;
  type: string;
  locality: string | null;
  cityName: string;
  stateName: string;
  stateSlug: string;
  citySlug: string;
  basePrice: number;
  averageRating: number | null;
  totalReviews: number;
  viewedAt: number; // timestamp
}

const STORAGE_KEY = 'lov_recently_viewed';
const DEFAULT_LIMIT = 12;

/**
 * Get recently viewed venues from localStorage
 */
function getStoredVenues(): RecentlyViewedVenue[] {
  if (typeof window === 'undefined') return [];

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

/**
 * Save recently viewed venues to localStorage
 */
function saveVenues(venues: RecentlyViewedVenue[]): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(venues));
  } catch {
    // Storage might be full, try clearing old data
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(venues.slice(0, 5)));
    } catch {
      // Ignore storage errors
    }
  }
}

export interface UseRecentlyViewedReturn {
  recentlyViewed: RecentlyViewedVenue[];
  addToRecentlyViewed: (venue: Omit<RecentlyViewedVenue, 'viewedAt'>) => void;
  removeFromRecentlyViewed: (venueId: string) => void;
  clearRecentlyViewed: () => void;
  isLoading: boolean;
}

export function useRecentlyViewed(limit = DEFAULT_LIMIT): UseRecentlyViewedReturn {
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedVenue[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load from localStorage on mount
  useEffect(() => {
    setRecentlyViewed(getStoredVenues());
    setIsLoading(false);
  }, []);

  /**
   * Add a venue to recently viewed
   */
  const addToRecentlyViewed = useCallback(
    (venue: Omit<RecentlyViewedVenue, 'viewedAt'>) => {
      setRecentlyViewed((prev) => {
        // Remove if already exists (will be re-added at top)
        const filtered = prev.filter((v) => v.id !== venue.id);

        // Add to beginning with timestamp
        const updated: RecentlyViewedVenue[] = [
          { ...venue, viewedAt: Date.now() },
          ...filtered,
        ].slice(0, limit);

        // Save to localStorage
        saveVenues(updated);

        return updated;
      });
    },
    [limit]
  );

  /**
   * Remove a venue from recently viewed
   */
  const removeFromRecentlyViewed = useCallback((venueId: string) => {
    setRecentlyViewed((prev) => {
      const updated = prev.filter((v) => v.id !== venueId);
      saveVenues(updated);
      return updated;
    });
  }, []);

  /**
   * Clear all recently viewed
   */
  const clearRecentlyViewed = useCallback(() => {
    setRecentlyViewed([]);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  return {
    recentlyViewed,
    addToRecentlyViewed,
    removeFromRecentlyViewed,
    clearRecentlyViewed,
    isLoading,
  };
}

/**
 * Helper to format venue data for storage
 */
export function formatVenueForRecentlyViewed(venue: {
  id: string;
  name: string;
  slug: string;
  coverImage?: string | null;
  venueType: string;
  locality?: string | null;
  city?: { name: string; slug?: string | null } | null;
  state?: { name: string; slug?: string | null } | null;
  basePrice: number;
  averageRating?: number | null;
  totalReviews?: number;
}): Omit<RecentlyViewedVenue, 'viewedAt'> {
  return {
    id: venue.id,
    name: venue.name,
    slug: venue.slug,
    coverImage: venue.coverImage || null,
    type: venue.venueType,
    locality: venue.locality || null,
    cityName: venue.city?.name || '',
    stateName: venue.state?.name || '',
    stateSlug: venue.state?.slug || '',
    citySlug: venue.city?.slug || '',
    basePrice: venue.basePrice,
    averageRating: venue.averageRating ?? null,
    totalReviews: venue.totalReviews ?? 0,
  };
}
